/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */

import React from 'react';
import { Box, Button, Card, CardContent, Typography, Alert, AlertTitle, Stack, Chip, Divider } from '@mui/material';
import { Refresh as RefreshIcon, BugReport as BugReportIcon, Home as HomeIcon, Report as ReportIcon } from '@mui/icons-material';
import { errorHandler } from '../utils/errorHandler';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error,
            errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        console.error('Error Boundary caught an error:', error, errorInfo);

        // Update state with error info
        this.setState({
            error,
            errorInfo
        });

        // Report error to error handler
        const parsedError = errorHandler.handle(error, {
            componentStack: errorInfo.componentStack,
            errorBoundary: true,
            errorId: this.state.errorId
        });

        // Store error for reporting
        this.setState({ parsedError });
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null,
            parsedError: null
        });
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    handleReportError = () => {
        const { error, errorInfo, errorId } = this.state;

        // Create error report
        const errorReport = {
            errorId,
            message: error?.message,
            stack: error?.stack,
            componentStack: errorInfo?.componentStack,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        };

        // Copy to clipboard
        navigator.clipboard
            .writeText(JSON.stringify(errorReport, null, 2))
            .then(() => {
                alert('Error report copied to clipboard. Please send it to support.');
            })
            .catch(() => {
                console.log('Error Report:', errorReport);
                alert('Error report logged to console. Please check developer tools.');
            });
    };

    render() {
        if (this.state.hasError) {
            const { error, errorInfo, parsedError, errorId } = this.state;
            const { fallback: FallbackComponent, showDetails = false } = this.props;

            // Use custom fallback component if provided
            if (FallbackComponent) {
                return (
                    <FallbackComponent
                        error={error}
                        errorInfo={errorInfo}
                        parsedError={parsedError}
                        errorId={errorId}
                        onRetry={this.handleRetry}
                        onGoHome={this.handleGoHome}
                        onReportError={this.handleReportError}
                    />
                );
            }

            // Default error UI
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        padding: 2,
                        backgroundColor: 'background.default'
                    }}
                >
                    <Card sx={{ maxWidth: 600, width: '100%' }}>
                        <CardContent>
                            <Stack spacing={3}>
                                {/* Error Header */}
                                <Box textAlign="center">
                                    <BugReportIcon
                                        sx={{
                                            fontSize: 64,
                                            color: 'error.main',
                                            mb: 2
                                        }}
                                    />
                                    <Typography variant="h4" component="h1" gutterBottom>
                                        Oops! Something went wrong
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        We're sorry, but something unexpected happened. Our team has been notified.
                                    </Typography>
                                </Box>

                                {/* Error ID */}
                                <Box textAlign="center">
                                    <Chip label={`Error ID: ${errorId}`} variant="outlined" size="small" color="error" />
                                </Box>

                                {/* User-friendly message */}
                                {parsedError && (
                                    <Alert severity="error">
                                        <AlertTitle>What happened?</AlertTitle>
                                        {parsedError.userMessage}
                                    </Alert>
                                )}

                                {/* Action Buttons */}
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleRetry} color="primary">
                                        Try Again
                                    </Button>
                                    <Button variant="outlined" startIcon={<HomeIcon />} onClick={this.handleGoHome}>
                                        Go Home
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ReportIcon />}
                                        onClick={this.handleReportError}
                                        color="secondary"
                                    >
                                        Report Issue
                                    </Button>
                                </Stack>

                                {/* Error Details (Development only) */}
                                {showDetails && process.env.NODE_ENV === 'development' && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                Error Details (Development)
                                            </Typography>

                                            {error && (
                                                <Box mb={2}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Error Message:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        component="pre"
                                                        sx={{
                                                            backgroundColor: 'grey.100',
                                                            padding: 1,
                                                            borderRadius: 1,
                                                            overflow: 'auto',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        {error.message}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {error?.stack && (
                                                <Box mb={2}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Stack Trace:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        component="pre"
                                                        sx={{
                                                            backgroundColor: 'grey.100',
                                                            padding: 1,
                                                            borderRadius: 1,
                                                            overflow: 'auto',
                                                            fontSize: '0.75rem',
                                                            maxHeight: 200
                                                        }}
                                                    >
                                                        {error.stack}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {errorInfo?.componentStack && (
                                                <Box>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Component Stack:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        component="pre"
                                                        sx={{
                                                            backgroundColor: 'grey.100',
                                                            padding: 1,
                                                            borderRadius: 1,
                                                            overflow: 'auto',
                                                            fontSize: '0.75rem',
                                                            maxHeight: 200
                                                        }}
                                                    >
                                                        {errorInfo.componentStack}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}

                                {/* Help Text */}
                                <Box textAlign="center">
                                    <Typography variant="body2" color="text.secondary">
                                        If this problem persists, please contact our support team with the Error ID above.
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>
            );
        }

        return this.props.children;
    }
}

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
    const WrappedComponent = (props) => (
        <ErrorBoundary {...errorBoundaryProps}>
            <Component {...props} />
        </ErrorBoundary>
    );

    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

    return WrappedComponent;
};

// Hook for error boundary context
export const useErrorBoundary = () => {
    const [error, setError] = React.useState(null);

    const resetError = React.useCallback(() => {
        setError(null);
    }, []);

    const captureError = React.useCallback((error) => {
        setError(error);
    }, []);

    React.useEffect(() => {
        if (error) {
            throw error;
        }
    }, [error]);

    return { captureError, resetError };
};

export default ErrorBoundary;
